
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Client } from '@/components/clients/types';
import { EnrichmentResponse } from '@/hooks/useCompanyEnrichment'; // Import EnrichmentResponse
import { EnrichedData } from '@/components/clients/enrichment-dialog/enrichmentTypes'; // For casting

interface UseEnrichmentDataManagerProps {
  client: Client | undefined | null;
}

// Helper to map client_enrichment_results to Partial<EnrichmentResponse>
const mapDbResultToEnrichmentResponse = (dbData: any): Partial<EnrichmentResponse> | null => {
  if (!dbData) return null;
  return {
    lutra_run_id: dbData.run_id,
    progress_stage: dbData.progress_stage,
    error_message: dbData.error_message,
    status: dbData.status,
    // Cast enriched_data if it exists, otherwise null
    result_data: dbData.enriched_data ? (dbData.enriched_data as EnrichedData) : null, 
    // message can be derived or set if available from dbData
    // debug can be set if available from dbData
  };
};

export const useEnrichmentDataManager = ({ client }: UseEnrichmentDataManagerProps) => {
  const [lastRunId, setLastRunId] = useState<string | undefined>(client?.last_enrichment_run_id || undefined);
  // Changed type from 'any' to 'Partial<EnrichmentResponse> | null'
  const [lastEnrichmentData, setLastEnrichmentData] = useState<Partial<EnrichmentResponse> | null>(null);

  const fetchEnrichmentDataByRunId = useCallback(async (clientId: string, runId: string): Promise<Partial<EnrichmentResponse> | null> => {
    if (!clientId || !runId) {
      setLastEnrichmentData(null);
      return null;
    }
    try {
      const { data, error } = await supabase
        .from('client_enrichment_results')
        .select('*')
        .eq('client_id', clientId)
        .eq('run_id', runId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn(`Failed to fetch enrichment data for run ${runId}:`, error.message);
        const errorResponse: Partial<EnrichmentResponse> = { error_message: error.message, progress_stage: 'error_fetching', status: 'error' };
        setLastEnrichmentData(errorResponse);
        return errorResponse; // Return the error state
      }
      
      const mappedData = mapDbResultToEnrichmentResponse(data);
      setLastEnrichmentData(mappedData);
      return mappedData;
    } catch (e: any) {
      console.warn(`Exception fetching enrichment data for run ${runId}:`, e.message);
      const errorResponse: Partial<EnrichmentResponse> = { error_message: e.message, progress_stage: 'error_fetching', status: 'error' };
      setLastEnrichmentData(errorResponse);
      return errorResponse; // Return the error state
    }
  }, []);

  useEffect(() => {
    if (client?.id && client.last_enrichment_run_id && !lastEnrichmentData) {
      fetchEnrichmentDataByRunId(client.id, client.last_enrichment_run_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, client?.last_enrichment_run_id, fetchEnrichmentDataByRunId]);

  useEffect(() => {
    if (client?.last_enrichment_run_id && client.last_enrichment_run_id !== lastRunId) {
      setLastRunId(client.last_enrichment_run_id);
      if (client.id) {
        fetchEnrichmentDataByRunId(client.id, client.last_enrichment_run_id);
      }
    }
  }, [client?.last_enrichment_run_id, client?.id, lastRunId, fetchEnrichmentDataByRunId]);

  return {
    lastRunId,
    setLastRunId,
    lastEnrichmentData, // Now typed as Partial<EnrichmentResponse> | null
    setLastEnrichmentData, // Expects Partial<EnrichmentResponse> | null
    fetchEnrichmentDataByRunId,
  };
};
